/* eslint indent: 0 */

import $ from 'jquery';
import loglevel from 'loglevel';
import autobind from 'class-autobind';
import { generateUuid } from 'zoomforth_zfutils/unique_identifiers.js';


// We need these functions because we can't b64encode a raw unicode string
// https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/btoa#Unicode_Strings
function utf8_to_b64(str) {
    return window.btoa(
      window.unescape(
        encodeURIComponent(str)
      )
    );
}


class Tracker {

  constructor(
    persistentEventData = {}, 
    { 
      baseTrackUrl,
      shouldNotTrack=false
    } = {}
  ) {

    if (shouldNotTrack) {
      loglevel.debug('`shouldNotTrack == true`, will not track any page events.');
    }

    this._isPersistentEventDataFinalized = false;
    this.persistentEventData = {
      referrer: null,
      page_session_uuid: generateUuid(),
      ...persistentEventData    
    };

    this.shouldNotTrack = shouldNotTrack;
    this.baseTrackUrl = baseTrackUrl;

    this._queuedEvents = [];
    this._tsFirstEvent = null;
    this._tsMostRecentEvent = null;

    autobind(this);
  }

  updatePersistentEventData(newPersistentData) {
    this.persistentEventData = {
      ...this.persistentEventData,
      ...newPersistentData
    };
  }

  /**
   * How long has it been since the FIRST event was processed (not sent)?
   * @returns {number} milliseconds
   */
  getMsSinceFirstEvent() {
    const tsFirstEvent = this._tsFirstEvent;
    const tsNow = (new Date()).getTime();
    // Calculate milliseconds since previous tracked lens interaction
    if (!tsFirstEvent) {
      return 0;
    }
    return tsNow - tsFirstEvent;
  }

  /**
   * How long has it been since the last event was processed (not sent)?
   * @returns {number} milliseconds
   */
  getMsSincePreviousEvent() {
    const tsMostRecentEvent = this._tsMostRecentEvent;
    const tsNow = new Date();
    if (!tsMostRecentEvent) {
      return 0;
    }
    return tsNow - tsMostRecentEvent;
  }


  /**
   * Sets this moment as the time stored about when we last processed an event
   */
  updateMostRecentEventTimeToNow() {
    const tsNow = (new Date()).getTime();
    if (!this._tsFirstEvent) {
      this._tsFirstEvent = tsNow;
    }
    this._tsMostRecentEvent = tsNow;
  }
  

  /**
   * When we finalize persistent event data, we send all queued
   * events (adding the current finalized persistent event data to each of them)
   * 
   * Notifications may occur as a result of the queue being sent
   */
  finalizePersistentDataAndSendQueue() {
    this._isPersistentEventDataFinalized = true;

    if (this.shouldNotTrack) {
      // Its possible for `shouldNotTrack` to change *after* we have queued
      // events, so we need to be sure to not send those queued events
      loglevel.debug('Persistent event data finalized, but not emitted because `shouldNotTrack == true`');
      return;
    }

    var eventListB64 = [];
    while (this._queuedEvents.length) {
      eventListB64.push(
        this.getEventAsBase64String(
          this.getEventDataWithPersistentData(
            this._queuedEvents.shift()
          )
        )
      );
    }

    loglevel.debug('Emitting queued events');
    // it is technically possible for this to not complete before a
    // subsequent track gets recorded, but that edge case shouldn't break anything
    $.ajax({
      url: '/_rpc/events/record_initial_queue', 
      data: JSON.stringify({
        'ms_time_since_first_when_sent': this.getMsSinceFirstEvent(),
        'events': eventListB64
      }), 
      type: 'POST',
      dataType: 'json' 
    }).fail(()=>{
      this.shouldNotTrack = true;
      loglevel.error('Failed to record initial track queue. Setting `shouldNotTrack` to `true`.');
    });

  }


  /**
   * @returns the combination of the current event data and the persistent event data
   */
  getEventDataWithPersistentData(eventData) {
    return {
      ...this.persistentEventData,
      ...eventData
    };
  }

  /**
   * We want to make any data a little less jarring for the casual
   * observer, so we don't send in plaintext. We base64 it first.
   */
  getEventAsBase64String(finalEventData) {
    return encodeURIComponent(utf8_to_b64(JSON.stringify(finalEventData)));
  }

  /**
   * @returns the URL for our tracker base don the event data
   */
  getEventUrl(finalData) {
    let url = this.baseTrackUrl + 
      '?data=' + this.getEventAsBase64String(finalData) +
      '&cb=' + Math.random();
    return url;
  }

  /**
   * Track an event with the analytics system
   * `currentData` is an object representing the data associated with the event
   */
  track(currentData, {isEventOnUnload = false} = {}) {

    if (this.shouldNotTrack) {
      loglevel.info(`Skipped tracking event at ${new Date()} (w/ action=${currentData.action}): `,  currentData); 
      return;
    }

    const dataWithTime = {
      ...currentData,
      since_previous: this.getMsSincePreviousEvent(),
      since_first: this.getMsSinceFirstEvent()
    };
    this.updateMostRecentEventTimeToNow();


    if (!this._isPersistentEventDataFinalized) {

      loglevel.debug(`Queueing event (w/ action=${dataWithTime.action}): `, dataWithTime);
      this._queuedEvents.push(dataWithTime);
  
    } else {
      const fullEventData = this.getEventDataWithPersistentData(dataWithTime);
      loglevel.debug(`Emitting event (w/ action=${fullEventData.action}): `, fullEventData);
      const url = this.getEventUrl(fullEventData);
      if (isEventOnUnload) {
        this.sendToEventUrlOnUnload(url);
      } else {
        this.sendToEventUrlAsPixel(url);
      }
  
    }
  }

  /**
   * Drop a pixel on the page with the URL
   * 
   * Why a tracking pixel?
   * See https://stackoverflow.com/questions/6638504/why-serve-1x1-pixel-gif-web-bugs-data-at-all
   */
  sendToEventUrlAsPixel(url) {
    var ourPixel = new Image();
    ourPixel.src = url;
  }

  /**
   * If we are in a page's onunload handler, we want to send
   * a beacon or a synchronous AJAX request.
   * See https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon#Description
   */
  sendToEventUrlOnUnload(url) {

    if (navigator.sendBeacon) {

      navigator.sendBeacon(url);

    } else {

      $.ajax({
        cache: false,
        type: 'POST',
        async: false,
        url: url
      });

    }
  }

}


export default Tracker;
